import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EmailIcon from "@material-ui/icons/Email";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import PlaceIcon from "@material-ui/icons/Place";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import uuidv4 from "uuid/v4";

import Divisions from "./Divisions";
import General from "./General";
import AlertDialog from "../Alert";
import LoadingButton from "../../Buttons/Loading";
import ConditionalTooltip from "../../ConditionalTooltip";
import * as constants from "../../../constants";
import * as actions from "../../../store/actions";
import { setUser } from "../../../store/features/users";
import * as appsyncOperations from "../../../utils/appsyncOperations";
import findDivisionRelations from "../../../utils/findDivisionRelations";
import { isUserRole } from "../../../utils/user";
import * as colors from "../../../styles/colors";

const UserDialog = ({ isOpen, onClose, user }) => {
  const dispatch = useDispatch();
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    content: "",
    onYes: () => null,
    onNo: () => null,
    onClose: () => null,
  });
  const [editMode, setEditMode] = useState({
    enabled: false,
    submitPending: false,
    onSubmit: () => null,
    onCancel: () => null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [selectedContent, setSelectedContent] = useState(content.GENERAL);
  const history = useHistory();
  const snackbar = useSnackbar();
  const enqueueSnackbar = snackbar.enqueueSnackbar;

  const handleContentChange = (content) => {
    setSelectedContent(content);
  };
  const enableEditMode = (onSubmit, onCancel) => {
    setEditMode({
      enabled: true,
      onSubmit: onSubmit,
      onCancel: (user) => {
        setEditMode({
          enabled: false,
          submitPending: false,
          onSubmit: () => null,
          onCancel: () => null,
        });
        onCancel(user);
      },
    });
  };
  const updateUser = async (input, onSuccess) => {
    setEditMode((prevState) => ({
      ...prevState,
      submitPending: true,
    }));
    try {
      const updatedUser = await appsyncOperations.updateServiceProviderUser({
        ...input,
        idempotentKey: uuidv4(),
      });
      setEditMode({
        enabled: false,
        submitPending: false,
        onSubmit: () => null,
        onCancel: () => null,
      });
      onSuccess(updatedUser);
      enqueueSnackbar("Edit saved!", { variant: "success" });
      dispatch(actions.users.setUser(updatedUser));
    } catch (error) {
      setEditMode((prevState) => ({
        ...prevState,
        submitPending: false,
      }));
      if (typeof error === "string") {
        enqueueSnackbar(error, { variant: "error" });
      } else {
        enqueueSnackbar("An unexpected error occurred.", { variant: "error" });
        console.warn(error);
      }
    }
  };
  const updateUserDivisions = async (input, onSuccess) => {
    setEditMode((prevState) => ({
      ...prevState,
      submitPending: true,
    }));
    try {
      const updatedDivisionRelations = await appsyncOperations.batchWriteDivisionRelations(
        input
      );
      setEditMode({
        enabled: false,
        submitPending: false,
        onSubmit: () => null,
        onCancel: () => null,
      });
      dispatch(
        actions.divisions.setDivisions({
          divisionRelations: updatedDivisionRelations,
        })
      );
      onSuccess(Object.keys(findDivisionRelations(user.userId).included));
      enqueueSnackbar("Edit saved!", { variant: "success" });
    } catch (error) {
      setEditMode((prevState) => ({
        ...prevState,
        submitPending: false,
      }));
      if (typeof error === "string") {
        enqueueSnackbar(error, { variant: "error" });
      } else {
        enqueueSnackbar("An unexpected error occurred.", { variant: "error" });
        console.warn(error);
      }
    }
  };
  const deleteUser = async () => {
    setAlertDialog({
      open: true,
      content: "Are you sure you want to delete this user?",
      onYes: async () => {
        setAlertDialog((prevState) => ({
          ...prevState,
          open: false,
        }));
        setDeleteLoading(true);
        try {
          const deletedUser = await appsyncOperations.deleteServiceProviderUser(
            {
              serviceProviderId: user.serviceProviderId,
              userId: user.userId,
            }
          );
          dispatch(setUser({ user: deletedUser }));
          enqueueSnackbar("User has been deleted!", { variant: "success" });
        } catch (error) {
          if (typeof error === "string") {
            enqueueSnackbar(error, { variant: "error" });
          } else {
            enqueueSnackbar("An unexpected error occurred.", {
              variant: "error",
            });
            console.warn(error);
          }
        }
        setDeleteLoading(false);
      },
      onNo: () => {
        setAlertDialog((prevState) => ({ ...prevState, open: false }));
      },
      onClose: () => {
        setAlertDialog((prevState) => ({ ...prevState, open: false }));
      },
    });
  };
  const restoreUser = async () => {
    setRestoreLoading(true);
    try {
      const restoredUser = await appsyncOperations.restoreServiceProviderUser({
        serviceProviderId: user.serviceProviderId,
        userId: user.userId,
      });
      dispatch(setUser({ user: restoredUser }));
      enqueueSnackbar("User has been restored!", { variant: "success" });
    } catch (error) {
      if (typeof error === "string") {
        enqueueSnackbar(error, { variant: "error" });
      } else {
        enqueueSnackbar("An unexpected error occurred.", { variant: "error" });
        console.warn(error);
      }
    }
    setRestoreLoading(false);
  };
  const resendTemporaryPassword = async () => {
    setResendLoading(true);
    try {
      await appsyncOperations.resendSPTempPass({ email: user.userId });
      enqueueSnackbar("Password has been resent!", { variant: "success" });
    } catch (error) {
      if (typeof error === "string") {
        enqueueSnackbar(error, { variant: "error" });
      } else {
        enqueueSnackbar("An unexpected error occurred.", { variant: "error" });
        console.warn(error);
      }
    }
    setResendLoading(false);
  };

  const isAdmin = isUserRole({ admin: true });
  const inputsDisabled =
    resendLoading || deleteLoading || restoreLoading || editMode.submitPending;
  const classes = useStyles();

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent classes={{ root: classes.dialogContent }}>
          <div className={classes.contentContainer}>
            <div className={classes.drawer}>
              <List>
                <Button
                  onClick={() => history.push(`/map?user=${user.userId}`)}
                  classes={{ label: classes.buttonLabel }}
                  color="primary"
                  disabled={
                    user.status === "OFFLINE" ||
                    editMode.enabled ||
                    inputsDisabled
                  }
                  fullWidth
                  startIcon={<PlaceIcon />}
                >
                  Locate
                </Button>
                <ConditionalTooltip
                  title={constants.helpText.adminOnlyAction}
                  visible={!isAdmin && !inputsDisabled}
                >
                  <span>
                    <Button
                      onClick={resendTemporaryPassword}
                      classes={{ label: classes.buttonLabel }}
                      color="primary"
                      disabled={!isAdmin || editMode.enabled || inputsDisabled}
                      fullWidth
                      startIcon={
                        resendLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <EmailIcon />
                        )
                      }
                    >
                      Resend Password
                    </Button>
                  </span>
                </ConditionalTooltip>
                <ConditionalTooltip
                  title={constants.helpText.adminOnlyAction}
                  visible={!isAdmin && !inputsDisabled}
                >
                  {!user.isDeleted ? (
                    <span>
                      <Button
                        onClick={deleteUser}
                        classes={{ label: classes.buttonLabel }}
                        color="primary"
                        disabled={
                          !isAdmin || editMode.enabled || inputsDisabled
                        }
                        fullWidth
                        startIcon={
                          deleteLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon />
                          )
                        }
                      >
                        Delete
                      </Button>
                    </span>
                  ) : (
                    <span>
                      <Button
                        onClick={restoreUser}
                        classes={{ label: classes.buttonLabel }}
                        color="primary"
                        disabled={
                          !isAdmin || editMode.enabled || inputsDisabled
                        }
                        fullWidth
                        startIcon={
                          restoreLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <PersonAddIcon />
                          )
                        }
                      >
                        Restore
                      </Button>
                    </span>
                  )}
                </ConditionalTooltip>
              </List>
              <Divider />
              <List>
                <ListSubheader
                  classes={{ gutters: classes.listItemGutters }}
                  disableSticky
                >
                  Settings
                </ListSubheader>
                <ListItem
                  classes={{ gutters: classes.listItemGutters }}
                  onClick={() => handleContentChange(content.GENERAL)}
                  button
                  dense
                  disabled={editMode.enabled || editMode.submitPending}
                  selected={selectedContent === content.GENERAL}
                >
                  <ListItemText>General</ListItemText>
                </ListItem>
                <ListItem
                  classes={{ gutters: classes.listItemGutters }}
                  onClick={() => handleContentChange(content.DIVISIONS)}
                  button
                  dense
                  disabled={editMode.enabled || editMode.submitPending}
                  selected={selectedContent === content.DIVISIONS}
                >
                  <ListItemText>Divisions</ListItemText>
                </ListItem>
              </List>
            </div>
            <div className={classes.content}>
              {selectedContent === content.GENERAL && (
                <General
                  user={user}
                  admin={isAdmin}
                  disabled={inputsDisabled}
                  onEdit={enableEditMode}
                  onSubmit={updateUser}
                />
              )}
              {selectedContent === content.DIVISIONS && (
                <Divisions
                  user={user}
                  admin={isAdmin}
                  disabled={inputsDisabled}
                  onEdit={enableEditMode}
                  onSubmit={updateUserDivisions}
                />
              )}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          {!editMode.enabled ? (
            <Button onClick={onClose} color="primary">
              Close
            </Button>
          ) : (
            <>
              <LoadingButton
                color="primary"
                onClick={editMode.onSubmit}
                isLoading={editMode.submitPending}
              >
                Save
              </LoadingButton>
              <Button onClick={() => editMode.onCancel(user)} color="primary">
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <AlertDialog
        content={alertDialog.content}
        isOpen={alertDialog.open}
        onYes={() => alertDialog.onYes(user)}
        onNo={alertDialog.onNo}
        onClose={alertDialog.onClose}
      />
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: 0,
  },
  contentContainer: {
    display: "flex",
  },
  content: {
    flex: 1,
    paddingBottom: 8,
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 8,
  },
  drawer: {
    width: 240,
    flexShrink: 0,
  },
  bannerTextButtonSeparator: {
    width: 16,
  },
  buttonLabel: {
    justifyContent: "flex-start",
    paddingLeft: 16,
  },
  responseButtonRoot: {
    color: colors.WHITE,
  },
  responseButtonOutlined: {
    border: `1px solid ${colors.WHITE}`,
  },
  listItemGutters: {
    paddingLeft: 24,
    paddingRight: 24,
  },
  bannerContainer: {
    marginBottom: 24,
  },
  bannerContent: {
    height: 72,
    display: "flex",
  },
  bannerText: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    marginLeft: 16,
    marginRight: 16,
    marginTop: 16,
  },
}));

const content = {
  DIVISIONS: "DIVISIONS",
  GENERAL: "GENERAL",
};

const styles = {
  menus: {
    width: 200,
  },
};

export default UserDialog;
